import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, useTranslation, useSettings } from '../App';
import { 
  LayoutDashboard, 
  FileText, 
  Settings, 
  Database,
  FormInput,
  CheckSquare,
  Shield,
  Users,
  Palette,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';

const Layout = () => {
  const { user, logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const { settings } = useSettings();
  const t = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.includes('/settings'));

  const mainNavItems = [
    { path: '/overview', icon: LayoutDashboard, label: 'overview' },
    { path: '/documents', icon: FileText, label: 'documents' },
  ];

  const settingsNavItems = [
    { path: '/settings/masterdata', icon: Database, label: 'masterdata' },
    { path: '/settings/form-config', icon: FormInput, label: 'formConfig' },
    { path: '/settings/approval-config', icon: CheckSquare, label: 'approvalConfig' },
    { path: '/settings/roles', icon: Shield, label: 'roles' },
    { path: '/settings/members', icon: Users, label: 'members' },
    { path: '/settings/brand', icon: Palette, label: 'brandSettings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.label}`}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200 ${
          isActive 
            ? 'bg-primary text-primary-foreground' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <Icon size={20} strokeWidth={1.5} />
        <span className="font-medium">{t(item.label)}</span>
      </Link>
    );
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? 'p-4' : ''}`}>
      {/* Logo */}
      <div className="px-4 py-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold font-['Outfit'] text-primary">DDS</h1>
        <p className="text-xs text-gray-500 mt-1">Dynamic Document System</p>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-3">
          {mainNavItems.map((item) => (
            <NavItem key={item.path} item={item} onClick={() => mobile && setSidebarOpen(false)} />
          ))}
          
          {/* Settings Dropdown */}
          <div className="pt-2">
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              data-testid="nav-settings-toggle"
              className={`w-full flex items-center justify-between px-4 py-2.5 rounded-md transition-all duration-200 ${
                location.pathname.includes('/settings')
                  ? 'text-primary bg-primary/10'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <Settings size={20} strokeWidth={1.5} />
                <span className="font-medium">{t('settings')}</span>
              </div>
              {settingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            
            {settingsOpen && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                {settingsNavItems.map((item) => (
                  <NavItem key={item.path} item={item} onClick={() => mobile && setSidebarOpen(false)} />
                ))}
              </div>
            )}
          </div>
        </nav>
      </ScrollArea>

      {/* User Section */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary text-primary-foreground font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 z-30 hidden md:flex flex-col">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-white z-50 transform transition-transform duration-300 md:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <Sidebar mobile />
      </aside>

      {/* Main Content */}
      <div className="md:ml-64">
        {/* Top Header */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-200/50">
          <div className="flex items-center justify-between px-4 md:px-6 h-16">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSidebarOpen(true); }}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </Button>

            <div className="flex-1" />

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="language-selector">
                    {language === 'en' ? 'EN' : 'ID'}
                    <ChevronDown size={16} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={(e) => { e.preventDefault(); changeLanguage('en'); }} 
                    data-testid="lang-en"
                  >
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => { e.preventDefault(); changeLanguage('id'); }} 
                    data-testid="lang-id"
                  >
                    Indonesia
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline">{user?.name}</span>
                    <ChevronDown size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut size={16} className="mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
